import mainStyle from "../../../mainStyle.module.scss";

import React, { useState, useEffect, useMemo } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from "lodash";

import { formLimitation } from 'const';
import CollapseCard from "components/Collapse";
import { MenuSelect } from "components/Select/menuSelect";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { Loading } from "components/Loading";
import { getApProfileOverrideValues } from "../../helper/main";

const rowStyle = {
  display: "flex",
  gap: 1,
  alignItems: "center",
  width: "100%",
};

const checkPvid = (value) => {
  if (value === "") {
    return false;
  }

  if (!/^\d+$/.test(value)) {
    return false;
  }

  const intValue = Number(value);
  if (intValue < 1 || intValue > 4094) {
    return false;
  }

  return true;
}

const checkAllowVlans = (value) => {
  if (!value?.trim()) return false;
  if (!/^[0-9,\-\s]+$/.test(value)) return false;
  const parts = value.split(",").map(s => s.trim());
  const used = new Set();
  const { min, max } = formLimitation.vlanId;

  for (let part of parts) {
    if (part === "") return false;

    if (part.includes("-")) {
      const [startStr, endStr] = part.split("-").map(s => s.trim());
      const start = parseInt(startStr, 10);
      const end = parseInt(endStr, 10);

      if (
        isNaN(start) || isNaN(end) ||
        start < min || end > max ||
        start > end
      ) return false;

      for (let i = start; i <= end; i++) {
        if (used.has(i)) return false;
        used.add(i);
      }

    } else {
      const num = parseInt(part, 10);
      if (isNaN(num) || num < min || num > max) return false;

      if (used.has(num)) return false;
      used.add(num);
    }
  }

  return true;
}

const SettingsDrawer = ({
  drawerOpen,
  setAlertInfo,
  activeRow,
  setActiveRow,
  originalRowData,
  setOriginalRowData,
  accessPointsData,
  setAccessPointsData,
  onUpdateBlockerData,
  profileAPList,
  showEditGeneral,
  vlanIdsCount,
}) => {
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(false);
  const [updateUuid, setUpdateUuid] = useState(uuidv4());
  const [isChanged, setIsChanged] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const isEditable = useMemo(() => !selectedProfile || selectedProfile?.isDefault, [selectedProfile]);
  const isProfileMode = useMemo(() => !selectedProfile?.isDefault, [selectedProfile?.isDefault]);
  const vlanSettingNumber = activeRow?.VLAN; // 0: Not support VLAN, 1: Only support single VLAN, 2: Port-Based VLAN

  const breadcrumbs = JSON.parse(localStorage.getItem("selectBreadcrumbs"));
  const hasManageOrg = breadcrumbs.realm?.hasManageOrg === true;
  const realmId = breadcrumbs.realm?.id ?? null;
  const orgsId = breadcrumbs.orgs?.id ?? null;
  const siteId = breadcrumbs.site?.id ?? null;
  let entId = null;
  let orgId = null;

  if (hasManageOrg) {
    entId = realmId;
    if (orgsId) orgId = orgsId;
  } else {
    orgId = realmId;
  }

  const isValidInputStyles = (isValid) => ({
    width: "64.5%",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.customBorderRadius[1],
      height: "31px",
      backgroundColor: theme.palette.primary.transparent,

      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.borderGreyPrimary
          : theme.palette.primary.redPrimary,
      },

      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.mainHover
          : theme.palette.primary.redPrimary,
      },

      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.main
          : theme.palette.primary.redPrimary,
      },
    },
  });

  const checkFormValid = () => {
    if (activeRow.portName === "") {
      return false;
    }

    if (!checkAllowVlans(activeRow.allowVlan?.trim())) {
      return false;
    }

    if (!checkPvid(activeRow.PVID?.toString().trim())) {
      return false;
    }

    if(vlanIdsCount.number > 128) {
      return false;
    }

    return true;
  }

  const handleCancel = () => {
    setActiveRow(originalRowData);

    const matchedProfile = profileAPList.find(profile => profile.value === originalRowData.portProfile) || null;
    setSelectedProfile(matchedProfile);
  };

  // const handleApply = () => {
  //   setIsLoading(true);

  //   const resourcePath =
  //     hasManageOrg && orgId
  //       ? `/ents/:entId/orgs/:orgId/sites/:siteId/settings/ports.update`
  //       : `/orgs/:orgId/sites/:siteId/settings/ports.update`;

  //   const restfulParams =
  //     hasManageOrg && orgId ? { entId, orgId, siteId } : { orgId, siteId };

  //   const requestParams = {
  //     updates: [
  //       {
  //         deviceId: activeRow.macAddress,
  //         "wired.port": [
  //           {
  //             id: activeRow.id,
  //             portIndex: activeRow.portIndex,
  //             deviceCategory: activeRow.deviceCategory,
  //             portTags: activeRow.portTags,
  //             portProfile: selectedProfile ? selectedProfile.value : "",
  //             portState: activeRow.portState,
  //             portType: activeRow.portType,
  //             PVID: activeRow.PVID,
  //             allowVlan: activeRow.allowVlan,
  //             poeState: activeRow.poeState,
  //             operation: {
  //               type: activeRow.operation?.type,
  //               mirrorPort: activeRow.operation?.mirrorPort ?? [],
  //               mirrorMode: activeRow.operation?.mirrorMode ?? "",
  //               aggregatePort: activeRow.operation?.aggregatePort ?? [],
  //               aggregateType: activeRow.operation?.aggregateType ?? "",
  //             },
  //             speed: activeRow.speed,
  //             ddpState: activeRow.ddpState,
  //             lbdState: activeRow.lbdState,
  //             portName: activeRow.portName
  //           },
  //         ],
  //       },
  //     ],
  //   };

  //   sendRequest(resourcePath, updateUuid, restfulParams, requestParams);
  //   setOriginalRowData(activeRow);
  //   setIsChanged(false);
  // };

  const onSave = () => {
    const updatedPort = getUpdatedPort();
    const newAccessPointsData = accessPointsData.map(loopAP => {

      if (updatedPort.id === loopAP.id) {
        return {
          ...updatedPort,
          isWaitingEdit: true
        }
      } else if (
        vlanSettingNumber === 1 &&
        updatedPort.portIndex === 1 &&
        loopAP.macAddress === updatedPort.macAddress
      ) {
        // Single VLAN model: all other ports of the same AP are linked to Port 1
        return {
          ...loopAP,
          portProfile: updatedPort.portProfile,
          portProfileName: updatedPort.portProfileName,
          PVID: updatedPort.PVID,
          allowVlan: updatedPort.allowVlan,
          isWaitingEdit: true
        }
      } else {
        return loopAP;
      }
    })
    setAccessPointsData(cloneDeep(newAccessPointsData));
    onUpdateBlockerData(newAccessPointsData);
    setOriginalRowData(cloneDeep(activeRow));

    setIsChanged(false);

    setAlertInfo({
      open: true,
      severity: "success",
      message: (
        <Box>
          <Box
            sx={{
              fontSize: theme.typography.customFontSize[3],
              fontWeight: theme.typography.customFontWeight[3],
              pb: 1
            }}
          >
            Changes Saved
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography>Settings saved to draft. Click</Typography>
            <Typography sx={{ fontWeight: theme.typography.customFontWeight[3] }}> "Apply Changes" </Typography>
            <Typography>to deploy them to the device.</Typography>
          </Box>
        </Box>
      )
    });

  };

  const getUpdatedPort = () => {
    const updatedPort = {
      ...activeRow,
      portProfile: selectedProfile ? selectedProfile.value : "",
      operation: {
        type: activeRow.operation?.type,
        mirrorPort: activeRow.operation?.mirrorPort ?? [],
        mirrorMode: activeRow.operation?.mirrorMode ?? "",
        aggregatePort: activeRow.operation?.aggregatePort ?? [],
        aggregateType: activeRow.operation?.aggregateType ?? "",
      },
    }

    return updatedPort;
  };

  useEffect(() => {
    if (!drawerOpen) {
      setSelectedProfile(null);
    }
  }, [drawerOpen]);

  useEffect(() => {
    const matchedProfile = profileAPList.find(profile => profile.value === activeRow.portProfile) || null;
    setSelectedProfile(matchedProfile);
  }, [activeRow?.portIndex, activeRow?.portProfile]);

  useEffect(() => {
    const changed = JSON.stringify(activeRow ?? {}) !== JSON.stringify(originalRowData ?? {});
    setIsChanged(changed);
  }, [activeRow, originalRowData]);

  return (
    <>
      { isLoading && <Loading/> }

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 2,
          px: 4,
          boxSizing: "border-box",
          gap: "20px",
        }}
      >
        <CollapseCard title="Basic">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {activeRow && (
              <>
                {/* Port Name */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Port Name
                  </Typography>
                  <TextField
                    type="text"
                    value={activeRow.portName}
                    /* disabled={true} */ // Port Name is read-only, when API support update, remove this line
                    onChange={(e) =>
                      setActiveRow({
                        ...activeRow,
                        portName: e.target.value,
                      })
                    }
                    sx={isValidInputStyles(activeRow.portName !== "")}
                  />
                  {(vlanSettingNumber === 0 && activeRow?.portIndex === 5) && (
                    <Typography marginLeft={2} sx={{ fontSize: theme.typography.customFontSize[2], color: theme.palette.primary.black, width: '202px', marginLeft: 0, marginRight: '20px' }}>
                      Port 5 (WAN/PoE)
                    </Typography>
                  )}
                </Box>
                {/* Port Profile */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Port Profile
                  </Typography>
                  <MenuSelect
                    value={selectedProfile?.value || ""}
                    disabled={!showEditGeneral || vlanSettingNumber === 0 || (vlanSettingNumber === 1 && activeRow?.portIndex !== 1)}
                    onChange={(opt) => {
                      const profile = profileAPList.find((item) => item.value === opt.value) || null;

                      setSelectedProfile(profile);

                      const overrides = getApProfileOverrideValues(profile);
                      setActiveRow((prev) => ({
                        ...prev,
                        ...overrides
                      }));
                    }}
                    options={profileAPList}
                    width="calc(100% - 185px)"
                  />
                </Box>
                {/* PVID */}
                <Box sx={rowStyle}>
                  <Typography
                    className={mainStyle["collapse-typography"]}
                    sx={{ alignSelf: "flex-start", pt: "6px" }}
                  >
                    PVID
                  </Typography>
                  {
                    isProfileMode &&
                    <TextField
                      disabled
                      type="number"
                      value={selectedProfile?.PVID}
                      sx={isValidInputStyles(true)}
                      helperText="(1~4094)"
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                    />
                  }

                  {
                    !isProfileMode &&
                    <TextField
                      disabled={!showEditGeneral || !isEditable || vlanSettingNumber === 0 || (vlanSettingNumber === 1 && activeRow?.portIndex !== 1)}
                      type="number"
                      value={activeRow.PVID}
                      onChange={(e) =>
                        setActiveRow({
                          ...activeRow,
                          PVID:
                            e.target.value === "" ? "" : Number(e.target.value),
                        })
                      }
                      sx={isValidInputStyles(checkPvid(activeRow.PVID?.toString().trim()))}
                      helperText="(1~4094)"
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                    />
                  }
                </Box>

                {
                  !isProfileMode &&
                  vlanIdsCount.number > 128 &&
                  <>
                    <Box
                      sx={{
                        ...rowStyle,
                        marginTop: "-1.3rem"
                      }}
                    >
                      <Typography
                        className={mainStyle["collapse-typography"]}
                        sx={{ alignSelf: "flex-start", pt: "6px" }}
                      >
                      </Typography>
                      <Typography
                        sx={{
                          color: theme.palette.primary.redPrimary,
                          fontSize: "0.75rem",
                        }}
                      >
                        Limit reached: Maximum 128 VLANs allowed.
                      </Typography>

                    </Box>
                  </>
                }

                {/* Allow VLAN */}
                <Box sx={rowStyle}>
                  <Typography
                    className={mainStyle["collapse-typography"]}
                    sx={{ alignSelf: "flex-start", pt: "6px" }}
                  >
                    Allow VLAN
                  </Typography>

                  {
                    isProfileMode &&
                    <TextField
                      disabled
                      type="text"
                      placeholder="e.g. 1,4-7"
                      value={selectedProfile?.allowVlan}
                      sx={isValidInputStyles(true)}
                      helperText={`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                    />
                  }

                  {
                    !isProfileMode &&
                    <TextField
                      disabled={!showEditGeneral || !isEditable || vlanSettingNumber === 0 || (vlanSettingNumber === 1 && activeRow?.portIndex !== 1)}
                      type="text"
                      placeholder="e.g. 1,4-7"
                      value={activeRow.allowVlan}
                      onChange={(e) =>
                        setActiveRow({
                          ...activeRow,
                          allowVlan: e.target.value,
                        })
                      }
                      sx={isValidInputStyles(checkAllowVlans(activeRow.allowVlan?.trim()))}
                      helperText={`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                    />
                  }
                </Box>

                {
                  !isProfileMode &&
                  vlanIdsCount.number > 128 &&
                  <>
                    <Box
                      sx={{
                        ...rowStyle,
                        marginTop: "-1.3rem"
                      }}
                    >
                      <Typography
                        className={mainStyle["collapse-typography"]}
                        sx={{ alignSelf: "flex-start", pt: "6px" }}
                      >
                      </Typography>
                      <Typography
                        sx={{
                          color: theme.palette.primary.redPrimary,
                          fontSize: "0.75rem",
                        }}
                      >
                        Limit reached: Maximum 128 VLANs allowed.
                      </Typography>

                    </Box>
                  </>
                }

                {(vlanSettingNumber === 1 && activeRow?.portIndex !== 1) && (
                  <Box>
                    <Typography sx={{ fontSize: theme.typography.customFontSize[2], color: theme.palette.primary.orange }}>
                      This port configuration is linked to Port 1. To modify this setting, select Port 1.
                    </Typography>
                  </Box>
                )}

              </>
            )}
          </Box>
        </CollapseCard>
        {isChanged && (
          <Box
            sx={{
              position: "sticky",
              width: "107%",
              display: "flex",
              justifyContent: "center",
              padding: "16px",
              backgroundColor: "primary.lightBlue",
              bottom: 0,
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
          >
            <OutlineButton
              onClick={handleCancel}
              sx={{ marginRight: "20px" }}
              width="100px"
              height="32px"
            >
              Revert
            </OutlineButton>
            <SolidButton
              disabled={!checkFormValid()}
              onClick={onSave}
              width="100px"
              height="32px"
            >
              Save
            </SolidButton>
          </Box>
        )}
      </Box>
    </>
  );
};
export default SettingsDrawer;
